import React, { useState, useEffect, useCallback } from 'react';
import { useSystem } from '@ohif/core';
import { Formik, Form } from 'formik';
import PageLayout from '../components/layouts/PageLayout';
import '../theme/common.css';
import './ViewProfile.css';
import { User as UserIcon, Mail, Shield, Camera, Activity, FileText } from 'lucide-react';
import { authService } from '../auth/authService';
import userService from '../services/userService';
import { mapApiResponseToFormValues, mapFormValuesToCreateRequest } from '../utils/userMapper';
import {
  getUserRole,
  type UserRoleType,
  isRadiologist,
  getRISRoleString,
} from '../utils/roleUtils';
import { getProfileValidationSchema } from '../validation';
import { isAllowedSignatureUploadFile, SIGNATURE_UPLOAD_TYPE_ERROR } from '../constants/constant';
import type { User } from '../types/user.types';
import {
  buildInitialValuesForRole,
  buildProfileJsonUpdateData,
  getProfileFieldError,
  notifyProfileResult,
  type ProfileApiResponse,
} from './viewProfileHelpers';
import {
  ViewProfileHeaderActions,
  ViewProfilePersonalDetailsSection,
  ViewProfileSecuritySection,
} from './viewProfileFormSections';

function initialsFromName(name: string): string {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatJoinDate(source: unknown): string {
  const s = String(source || '').trim();
  if (!s) {
    return '—';
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) {
    return s;
  }
  // e.g. "16 December 2025" (day, full month, year)
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

function formatDepartmentLabel(part: string): string {
  const s = part.trim();
  if (s.toLowerCase() === 'radiologist') {
    return 'Radiology';
  }
  return s || 'Radiology';
}

function formatDepartmentDisplay(department: unknown): string {
  if (Array.isArray(department) && department.length > 0) {
    return department
      .map(d => formatDepartmentLabel(String(d)))
      .filter(Boolean)
      .join(', ');
  }
  if (typeof department === 'string' && department.trim()) {
    return formatDepartmentLabel(department);
  }
  return 'Radiology';
}

function formatPhoneDisplay(phone: unknown): string {
  const digits = String(phone || '')
    .replace(/\D/g, '')
    .slice(-10);
  if (digits.length === 10) {
    return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
  }
  return String(phone || '').trim() || '—';
}

/** Inline errors for password row; `mode` save = only validate if any field is non-empty */
type ProfilePasswordErrors = {
  current?: string;
  new?: string;
  confirm?: string;
};

function getProfilePasswordErrors(
  current: string,
  next: string,
  confirm: string,
  mode: 'save' | 'update'
): ProfilePasswordErrors | null {
  const c = current.trim();
  const n = next.trim();
  const p = confirm.trim();
  const any = Boolean(c || n || p);
  if (mode === 'save' && !any) {
    return null;
  }
  const err: ProfilePasswordErrors = {};
  if (!c) {
    err.current = 'This field is required';
  }
  if (!n) {
    err.new = 'This field is required';
  }
  if (!p) {
    err.confirm = 'This field is required';
  }
  if (c && n && p && n !== p) {
    err.confirm = 'Passwords do not match';
  }
  if (err.current == null && err.new == null && err.confirm == null) {
    return null;
  }
  return err;
}

interface CurrentUserResponse extends ProfileApiResponse {}

type ViewProfileFormBodyProps = {
  values: any;
  handleChange: any;
  setFieldValue: any;
  handleSubmit: any;
  resetForm: any;
  isValid: boolean;
  dirty: boolean;
  getFieldError: (fieldName: string) => string | undefined;
  isEditing: boolean;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
  isSubmitting: boolean;
  clearPasswordFields: () => void;
  userData: any;
  pwCurrent: string;
  setPwCurrent: React.Dispatch<React.SetStateAction<string>>;
  pwNew: string;
  setPwNew: React.Dispatch<React.SetStateAction<string>>;
  pwConfirm: string;
  setPwConfirm: React.Dispatch<React.SetStateAction<string>>;
  pwErrors: any;
  setPwErrors: any;
  isPwSubmitting: boolean;
  handleUpdatePassword: () => void | Promise<void>;
};

function ViewProfileFormBody({
  values,
  handleChange,
  setFieldValue,
  handleSubmit,
  resetForm,
  isValid,
  dirty,
  getFieldError,
  isEditing,
  setIsEditing,
  isSubmitting,
  clearPasswordFields,
  userData,
  pwCurrent,
  setPwCurrent,
  pwNew,
  setPwNew,
  pwConfirm,
  setPwConfirm,
  pwErrors,
  setPwErrors,
  isPwSubmitting,
  handleUpdatePassword,
}: Readonly<ViewProfileFormBodyProps>) {
  return (
    <Form>
      <div className="view-profile-page">
        {/* Local subheader (like Facility/User Mgmt) */}
        <div className="sticky top-0 z-20 shrink-0 bg-white shadow-sm">
          <div className="flex h-14 items-center justify-between border-b border-[#e5e5e5] px-6">
            <div className="flex min-w-0 items-center gap-3">
              <h1 className="truncate text-[18px] leading-[28px] text-[#0a0a0a]">
                <span className="font-bold">Profile</span>
              </h1>
            </div>
            <ViewProfileHeaderActions
              isEditing={isEditing}
              isSubmitting={isSubmitting}
              isValid={isValid}
              dirty={dirty}
              resetForm={resetForm}
              clearPasswordFields={clearPasswordFields}
              setIsEditing={setIsEditing}
            />
          </div>
        </div>

        <div className="view-profile-content-container">
          <div className="view-profile-main-column">
            {/* Summary */}
            <section className="view-profile-card">
              <div className="view-profile-summary">
                <div className="view-profile-avatar-wrap">
                  <div className="view-profile-avatar">
                    {initialsFromName(String(values.fullName || ''))}
                  </div>
                  <div
                    className="view-profile-avatar-camera"
                    title="Photo"
                    aria-hidden
                  >
                    <Camera size={14} />
                  </div>
                </div>
                <div className="view-profile-summary-meta">
                  <div className="view-profile-summary-line">
                    <span
                      className="view-profile-summary-icon-wrap"
                      title="Username"
                      aria-label="Username"
                    >
                      <UserIcon
                        size={16}
                        strokeWidth={2}
                      />
                    </span>
                    <span className="view-profile-summary-v">
                      {String(
                        values.username ||
                          userData?.data?.username ||
                          userData?.fromDatabase?.username ||
                          ''
                      )}
                    </span>
                  </div>
                  <div className="view-profile-summary-line">
                    <span
                      className="view-profile-summary-icon-wrap"
                      title="Email"
                      aria-label="Email"
                    >
                      <Mail
                        size={16}
                        strokeWidth={2}
                      />
                    </span>
                    <span className="view-profile-summary-v">{String(values.email || '')}</span>
                  </div>
                  <div className="view-profile-summary-line">
                    <span
                      className="view-profile-summary-icon-wrap"
                      title="Role"
                      aria-label="Role"
                    >
                      <Shield
                        size={16}
                        strokeWidth={2}
                      />
                    </span>
                    <span className="view-profile-summary-v">
                      {String(userData?.fromDatabase?.role || '')}
                    </span>
                  </div>
                </div>
              </div>
            </section>

            <ViewProfilePersonalDetailsSection
              values={values}
              userData={userData}
              isEditing={isEditing}
              handleChange={handleChange}
              setFieldValue={setFieldValue}
              getFieldError={getFieldError}
              formatPhoneDisplay={formatPhoneDisplay}
              formatDepartmentDisplay={formatDepartmentDisplay}
              formatJoinDate={formatJoinDate}
              getFacilityName={() => authService.getFacility()?.facility_name || '—'}
            />

            <ViewProfileSecuritySection
              isEditing={isEditing}
              pwCurrent={pwCurrent}
              setPwCurrent={setPwCurrent}
              pwNew={pwNew}
              setPwNew={setPwNew}
              pwConfirm={pwConfirm}
              setPwConfirm={setPwConfirm}
              pwErrors={pwErrors}
              setPwErrors={setPwErrors}
              isPwSubmitting={isPwSubmitting}
              handleUpdatePassword={handleUpdatePassword}
            />

            {/* Recent Activity */}
            <section className="view-profile-card view-profile-card--section">
              <div className="view-profile-card-section-head">
                <h3 className="view-profile-card-section-title">Recent Activity</h3>
                <hr className="view-profile-card-section-rule" />
              </div>
              <div className="view-profile-card-section-body">
                <div className="view-profile-activity-grid">
                  <div className="view-profile-activity-item">
                    <div className="view-profile-activity-icon view-profile-activity-icon--green">
                      <Activity size={20} />
                    </div>
                    <div className="view-profile-activity-text">
                      <strong>Logged in successfully</strong>
                      <span>Today at 9:00 AM</span>
                    </div>
                  </div>
                  <div className="view-profile-activity-item">
                    <div className="view-profile-activity-icon view-profile-activity-icon--blue">
                      <FileText size={20} />
                    </div>
                    <div className="view-profile-activity-text">
                      <strong>Completed report for Case #12345</strong>
                      <span>Yesterday at 5:45 PM</span>
                    </div>
                  </div>
                  <div className="view-profile-activity-item">
                    <div className="view-profile-activity-icon view-profile-activity-icon--purple">
                      <FileText size={20} />
                    </div>
                    <div className="view-profile-activity-text">
                      <strong>Reviewed 8 cases</strong>
                      <span>Yesterday at 2:30 PM</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </Form>
  );
}

const ViewProfile: React.FC = () => {
  const { servicesManager } = useSystem();
  const { uiNotificationService } = servicesManager.services;

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [isPwSubmitting, setIsPwSubmitting] = useState(false);
  const [pwErrors, setPwErrors] = useState<ProfilePasswordErrors | null>(null);
  const [userData, setUserData] = useState<CurrentUserResponse | null>(null);
  const [userRole, setUserRole] = useState<UserRoleType>('Admin');
  // Formik values are heterogeneous by role; keep them permissive here
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [initialValues, setInitialValues] = useState<any>(null);
  const fetchUserData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await authService.getCurrentUser();
      if (response.success) {
        const apiResponse: CurrentUserResponse = {
          success: true,
          data: response.data as CurrentUserResponse['data'],
          fromDatabase: response.fromDatabase as User,
        };
        setUserData(apiResponse);

        const dataRoles = (apiResponse.data as { roles?: string[] })?.roles;
        const role = getUserRole(dataRoles, apiResponse.fromDatabase);
        setUserRole(role);

        if (apiResponse.fromDatabase) {
          const mapped = mapApiResponseToFormValues(apiResponse.fromDatabase);
          setInitialValues(buildInitialValuesForRole(role, apiResponse, mapped));
        }
      } else {
        notifyProfileResult(
          uiNotificationService,
          false,
          '',
          response.error || 'Failed to load profile data'
        );
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      notifyProfileResult(uiNotificationService, false, '', 'Failed to load profile data');
    } finally {
      setIsLoading(false);
    }
  }, [uiNotificationService]);

  // Fetch user data on component mount
  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // Get validation schema based on role
  const getValidationSchema = () => {
    return getProfileValidationSchema(userRole);
  };

  const PROFILE_BREADCRUMBS = useCallback(
    () => [{ label: 'Home', path: '/' }, { label: 'Profile' }],
    []
  );

  const clearPasswordFields = useCallback(() => {
    setPwCurrent('');
    setPwNew('');
    setPwConfirm('');
    setPwErrors(null);
  }, []);

  const handleUpdatePassword = async () => {
    if (!userData?.fromDatabase?.id) {
      return;
    }
    const errs = getProfilePasswordErrors(pwCurrent, pwNew, pwConfirm, 'update');
    if (errs) {
      setPwErrors(errs);
      return;
    }
    setPwErrors(null);
    setIsPwSubmitting(true);
    try {
      const res = await authService.updatePassword(pwNew, userData.fromDatabase.id);
      if (res.success) {
        uiNotificationService?.show({
          title: 'Success',
          message: 'Password updated successfully',
          type: 'success',
          duration: 3000,
        });
        clearPasswordFields();
      } else {
        uiNotificationService?.show({
          title: 'Error',
          message: res.error || 'Failed to update password',
          type: 'error',
          duration: 5000,
        });
      }
    } catch {
      uiNotificationService?.show({
        title: 'Error',
        message: 'Failed to update password',
        type: 'error',
        duration: 5000,
      });
    } finally {
      setIsPwSubmitting(false);
    }
  };

  const handleSubmit = async (values: Record<string, unknown>) => {
    if (!userData?.fromDatabase) {
      return;
    }

    const user = userData.fromDatabase as User;
    if (!user.id) {
      return;
    }

    setIsSubmitting(true);
    try {
      const hasSignatureFile = values.signature instanceof File;

      if (
        hasSignatureFile &&
        isRadiologist(userRole) &&
        !isAllowedSignatureUploadFile(values.signature as File)
      ) {
        uiNotificationService?.show({
          title: 'Invalid file',
          message: SIGNATURE_UPLOAD_TYPE_ERROR,
          type: 'error',
          duration: 5000,
        });
        setIsSubmitting(false);
        return;
      }

      let response: { success: boolean; message?: string; error?: string; data?: User };

      if (hasSignatureFile && isRadiologist(userRole)) {
        const facilityId = authService.getFacilityId() || user.facility_id;
        const currentUserRole = getRISRoleString(authService.getUser());
        const formData = await mapFormValuesToCreateRequest(
          {
            fullName: values.fullName as string,
            gender: values.gender as string,
            dateOfBirth: values.dateOfBirth as string,
            username: values.username as string,
            email: values.email as string,
            phoneNo: values.phoneNo as string,
            password: '',
            role: 'Radiologist',
            status: user.status || 'Active',
            employeeId: user.doctor_id || '',
            qualification: values.qualification as string,
            experienceYears: values.experienceYears as number | string,
            registrationNo: values.registrationNo as string,
            specialty: String(values.specialty || '')
              .split(',')
              .map(s => s.trim())
              .filter(Boolean),
            designation: values.designation as string,
            reportingModalityAccess: values.reportingModalityAccess as string[],
            signature: values.signature as File,
            availabilityHours: values.availabilityHours as string[],
            availabilityDays: values.availabilityDays as string[],
          },
          facilityId,
          true,
          currentUserRole
        );
        response = await userService.updateUser(user.id, formData);
      } else {
        const updateData = buildProfileJsonUpdateData(values, userRole);
        response = await userService.updateUser(user.id, updateData);
      }

      notifyProfileResult(
        uiNotificationService,
        response.success,
        response.message || 'Profile updated successfully',
        response.error || 'Failed to update profile'
      );
      if (response.success) {
        await fetchUserData();
        clearPasswordFields();
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      notifyProfileResult(uiNotificationService, false, '', 'Failed to update profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || !initialValues) {
    return (
      <PageLayout
        title=""
        hideTitleInHeader
        breadcrumbs={PROFILE_BREADCRUMBS()}
        contentClassName="view-profile-page-layout-content"
      >
        <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>
          Loading profile data...
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title=""
      hideTitleInHeader
      breadcrumbs={PROFILE_BREADCRUMBS()}
      contentClassName="view-profile-page-layout-content"
    >
      <Formik<any>
        initialValues={initialValues}
        validationSchema={getValidationSchema()}
        onSubmit={handleSubmit}
        enableReinitialize={true}
        validateOnMount={true}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          setFieldValue,
          setFieldError,
          setFieldTouched,
          handleSubmit,
          resetForm,
          isValid,
          dirty,
        }) => {
          const getFieldError = (fieldName: string) =>
            getProfileFieldError(
              errors as Record<string, unknown>,
              touched as Record<string, unknown>,
              values as Record<string, unknown>,
              fieldName
            );
          return (
            <ViewProfileFormBody
              values={values}
              handleChange={handleChange}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
              resetForm={resetForm}
              isValid={isValid}
              dirty={dirty}
              getFieldError={getFieldError}
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              isSubmitting={isSubmitting}
              clearPasswordFields={clearPasswordFields}
              userData={userData}
              pwCurrent={pwCurrent}
              setPwCurrent={setPwCurrent}
              pwNew={pwNew}
              setPwNew={setPwNew}
              pwConfirm={pwConfirm}
              setPwConfirm={setPwConfirm}
              pwErrors={pwErrors}
              setPwErrors={setPwErrors}
              isPwSubmitting={isPwSubmitting}
              handleUpdatePassword={handleUpdatePassword}
            />
          );
        }}
      </Formik>
    </PageLayout>
  );
};

export default ViewProfile;
